/**
 * Candidate Registration Page - Updated Structure
 * Multi-step registration form: Personal Info, Professional Details, Documents
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import { STATES, getDistrictsByState } from '../constants/locations';

const WORK_EXPERIENCE_OPTIONS = ['0', '1', '1.5', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23', '24', '25+'];
const EDUCATION_OPTIONS = ['Doctorate', 'Masters', 'Bachelor Degree', 'Diploma'];
const GENDER_OPTIONS = ['Male', 'Female', 'Other'];
const REGISTRATION_DOCUMENT_FIELDS = [
  { key: 'passportDocument', label: 'Passport', required: true },
  { key: 'stateRegistrationDocument', label: 'All State Registration Certificates including Renewal (RNRM)', required: true },
  { key: 'practicingLicenseDocument', label: 'Practicing License/TN Council ID Card', required: false },
  { key: 'degreeDiplomaDocument', label: 'Degree/Diploma Certificate', required: true },
  { key: 'transcriptDocument', label: 'Transcript & College Marksheet', required: true },
  { key: 'schoolCertificatesDocument', label: 'School Certificates (HSC/SSLC)', required: true },
  { key: 'experienceCertificatesDocument', label: 'All Experience Certificates', required: true },
  { key: 'marriageBirthCertificateDocument', label: 'Marriage/Birth Certificate in English', required: true },
  { key: 'aadharCardDocument', label: 'Aadhar Card', required: true },
  { key: 'affidavitsDocument', label: 'Affidavits', required: false }
];

const CandidateRegistrationPage = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const navigate = useNavigate();

  // Email OTP verification state
  const [emailVerified, setEmailVerified] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpValue, setOtpValue] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [resendCountdown, setResendCountdown] = useState(0);

  const countryCodeOptions = [
    { code: '+93', label: 'Afghanistan (+93)' },
    { code: '+355', label: 'Albania (+355)' },
    { code: '+213', label: 'Algeria (+213)' },
    { code: '+376', label: 'Andorra (+376)' },
    { code: '+244', label: 'Angola (+244)' },
    { code: '+54', label: 'Argentina (+54)' },
    { code: '+374', label: 'Armenia (+374)' },
    { code: '+61', label: 'Australia (+61)' },
    { code: '+43', label: 'Austria (+43)' },
    { code: '+994', label: 'Azerbaijan (+994)' },
    { code: '+973', label: 'Bahrain (+973)' },
    { code: '+880', label: 'Bangladesh (+880)' },
    { code: '+375', label: 'Belarus (+375)' },
    { code: '+32', label: 'Belgium (+32)' },
    { code: '+501', label: 'Belize (+501)' },
    { code: '+229', label: 'Benin (+229)' },
    { code: '+975', label: 'Bhutan (+975)' },
    { code: '+591', label: 'Bolivia (+591)' },
    { code: '+387', label: 'Bosnia and Herzegovina (+387)' },
    { code: '+267', label: 'Botswana (+267)' },
    { code: '+55', label: 'Brazil (+55)' },
    { code: '+673', label: 'Brunei (+673)' },
    { code: '+359', label: 'Bulgaria (+359)' },
    { code: '+226', label: 'Burkina Faso (+226)' },
    { code: '+257', label: 'Burundi (+257)' },
    { code: '+855', label: 'Cambodia (+855)' },
    { code: '+237', label: 'Cameroon (+237)' },
    { code: '+1', label: 'Canada (+1)' },
    { code: '+238', label: 'Cape Verde (+238)' },
    { code: '+236', label: 'Central African Republic (+236)' },
    { code: '+235', label: 'Chad (+235)' },
    { code: '+56', label: 'Chile (+56)' },
    { code: '+86', label: 'China (+86)' },
    { code: '+57', label: 'Colombia (+57)' },
    { code: '+269', label: 'Comoros (+269)' },
    { code: '+242', label: 'Congo (+242)' },
    { code: '+506', label: 'Costa Rica (+506)' },
    { code: '+385', label: 'Croatia (+385)' },
    { code: '+53', label: 'Cuba (+53)' },
    { code: '+357', label: 'Cyprus (+357)' },
    { code: '+420', label: 'Czech Republic (+420)' },
    { code: '+45', label: 'Denmark (+45)' },
    { code: '+253', label: 'Djibouti (+253)' },
    { code: '+593', label: 'Ecuador (+593)' },
    { code: '+20', label: 'Egypt (+20)' },
    { code: '+503', label: 'El Salvador (+503)' },
    { code: '+240', label: 'Equatorial Guinea (+240)' },
    { code: '+291', label: 'Eritrea (+291)' },
    { code: '+372', label: 'Estonia (+372)' },
    { code: '+251', label: 'Ethiopia (+251)' },
    { code: '+679', label: 'Fiji (+679)' },
    { code: '+358', label: 'Finland (+358)' },
    { code: '+33', label: 'France (+33)' },
    { code: '+241', label: 'Gabon (+241)' },
    { code: '+220', label: 'Gambia (+220)' },
    { code: '+995', label: 'Georgia (+995)' },
    { code: '+49', label: 'Germany (+49)' },
    { code: '+233', label: 'Ghana (+233)' },
    { code: '+30', label: 'Greece (+30)' },
    { code: '+502', label: 'Guatemala (+502)' },
    { code: '+224', label: 'Guinea (+224)' },
    { code: '+245', label: 'Guinea-Bissau (+245)' },
    { code: '+592', label: 'Guyana (+592)' },
    { code: '+509', label: 'Haiti (+509)' },
    { code: '+504', label: 'Honduras (+504)' },
    { code: '+852', label: 'Hong Kong (+852)' },
    { code: '+36', label: 'Hungary (+36)' },
    { code: '+354', label: 'Iceland (+354)' },
    { code: '+91', label: 'India (+91)' },
    { code: '+62', label: 'Indonesia (+62)' },
    { code: '+98', label: 'Iran (+98)' },
    { code: '+964', label: 'Iraq (+964)' },
    { code: '+353', label: 'Ireland (+353)' },
    { code: '+972', label: 'Israel (+972)' },
    { code: '+39', label: 'Italy (+39)' },
    { code: '+225', label: 'Ivory Coast (+225)' },
    { code: '+81', label: 'Japan (+81)' },
    { code: '+962', label: 'Jordan (+962)' },
    { code: '+7', label: 'Kazakhstan (+7)' },
    { code: '+254', label: 'Kenya (+254)' },
    { code: '+965', label: 'Kuwait (+965)' },
    { code: '+996', label: 'Kyrgyzstan (+996)' },
    { code: '+856', label: 'Laos (+856)' },
    { code: '+371', label: 'Latvia (+371)' },
    { code: '+961', label: 'Lebanon (+961)' },
    { code: '+266', label: 'Lesotho (+266)' },
    { code: '+231', label: 'Liberia (+231)' },
    { code: '+218', label: 'Libya (+218)' },
    { code: '+423', label: 'Liechtenstein (+423)' },
    { code: '+370', label: 'Lithuania (+370)' },
    { code: '+352', label: 'Luxembourg (+352)' },
    { code: '+853', label: 'Macau (+853)' },
    { code: '+389', label: 'Macedonia (+389)' },
    { code: '+261', label: 'Madagascar (+261)' },
    { code: '+265', label: 'Malawi (+265)' },
    { code: '+60', label: 'Malaysia (+60)' },
    { code: '+960', label: 'Maldives (+960)' },
    { code: '+223', label: 'Mali (+223)' },
    { code: '+356', label: 'Malta (+356)' },
    { code: '+222', label: 'Mauritania (+222)' },
    { code: '+230', label: 'Mauritius (+230)' },
    { code: '+52', label: 'Mexico (+52)' },
    { code: '+373', label: 'Moldova (+373)' },
    { code: '+377', label: 'Monaco (+377)' },
    { code: '+976', label: 'Mongolia (+976)' },
    { code: '+382', label: 'Montenegro (+382)' },
    { code: '+212', label: 'Morocco (+212)' },
    { code: '+258', label: 'Mozambique (+258)' },
    { code: '+95', label: 'Myanmar (+95)' },
    { code: '+264', label: 'Namibia (+264)' },
    { code: '+977', label: 'Nepal (+977)' },
    { code: '+31', label: 'Netherlands (+31)' },
    { code: '+64', label: 'New Zealand (+64)' },
    { code: '+505', label: 'Nicaragua (+505)' },
    { code: '+227', label: 'Niger (+227)' },
    { code: '+234', label: 'Nigeria (+234)' },
    { code: '+47', label: 'Norway (+47)' },
    { code: '+968', label: 'Oman (+968)' },
    { code: '+92', label: 'Pakistan (+92)' },
    { code: '+970', label: 'Palestine (+970)' },
    { code: '+507', label: 'Panama (+507)' },
    { code: '+675', label: 'Papua New Guinea (+675)' },
    { code: '+595', label: 'Paraguay (+595)' },
    { code: '+51', label: 'Peru (+51)' },
    { code: '+63', label: 'Philippines (+63)' },
    { code: '+48', label: 'Poland (+48)' },
    { code: '+351', label: 'Portugal (+351)' },
    { code: '+974', label: 'Qatar (+974)' },
    { code: '+40', label: 'Romania (+40)' },
    { code: '+7', label: 'Russia (+7)' },
    { code: '+250', label: 'Rwanda (+250)' },
    { code: '+966', label: 'Saudi Arabia (+966)' },
    { code: '+221', label: 'Senegal (+221)' },
    { code: '+381', label: 'Serbia (+381)' },
    { code: '+248', label: 'Seychelles (+248)' },
    { code: '+232', label: 'Sierra Leone (+232)' },
    { code: '+65', label: 'Singapore (+65)' },
    { code: '+421', label: 'Slovakia (+421)' },
    { code: '+386', label: 'Slovenia (+386)' },
    { code: '+677', label: 'Solomon Islands (+677)' },
    { code: '+252', label: 'Somalia (+252)' },
    { code: '+27', label: 'South Africa (+27)' },
    { code: '+82', label: 'South Korea (+82)' },
    { code: '+211', label: 'South Sudan (+211)' },
    { code: '+34', label: 'Spain (+34)' },
    { code: '+94', label: 'Sri Lanka (+94)' },
    { code: '+249', label: 'Sudan (+249)' },
    { code: '+597', label: 'Suriname (+597)' },
    { code: '+268', label: 'Swaziland (+268)' },
    { code: '+46', label: 'Sweden (+46)' },
    { code: '+41', label: 'Switzerland (+41)' },
    { code: '+963', label: 'Syria (+963)' },
    { code: '+886', label: 'Taiwan (+886)' },
    { code: '+992', label: 'Tajikistan (+992)' },
    { code: '+255', label: 'Tanzania (+255)' },
    { code: '+66', label: 'Thailand (+66)' },
    { code: '+670', label: 'Timor-Leste (+670)' },
    { code: '+228', label: 'Togo (+228)' },
    { code: '+676', label: 'Tonga (+676)' },
    { code: '+216', label: 'Tunisia (+216)' },
    { code: '+90', label: 'Turkey (+90)' },
    { code: '+993', label: 'Turkmenistan (+993)' },
    { code: '+256', label: 'Uganda (+256)' },
    { code: '+380', label: 'Ukraine (+380)' },
    { code: '+971', label: 'United Arab Emirates (+971)' },
    { code: '+44', label: 'United Kingdom (+44)' },
    { code: '+1', label: 'United States (+1)' },
    { code: '+598', label: 'Uruguay (+598)' },
    { code: '+998', label: 'Uzbekistan (+998)' },
    { code: '+678', label: 'Vanuatu (+678)' },
    { code: '+58', label: 'Venezuela (+58)' },
    { code: '+84', label: 'Vietnam (+84)' },
    { code: '+967', label: 'Yemen (+967)' },
    { code: '+260', label: 'Zambia (+260)' },
    { code: '+263', label: 'Zimbabwe (+263)' }
  ];

  const [formData, setFormData] = useState({
    name: '',
    passportNumber: '',
    gender: '',
    state: '',
    district: '',
    mobile: '',
    email: '',
    password: '',
    confirmPassword: '',
    dateOfBirth: '',
    profilePhoto: null,
    currentJobRole: '',
    workExperience: '',
    education: '',
    resume: null,
    passportDocument: null,
    stateRegistrationDocument: null,
    practicingLicenseDocument: null,
    degreeDiplomaDocument: null,
    transcriptDocument: null,
    schoolCertificatesDocument: null,
    experienceCertificatesDocument: null,
    marriageBirthCertificateDocument: null,
    aadharCardDocument: null,
    affidavitsDocument: null
  });

  const [districts, setDistricts] = useState([]);
  const totalSteps = 4;

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'state') {
      setDistricts(getDistrictsByState(value));
      setFormData(prev => ({ ...prev, state: value, district: '' }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    setError('');
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (files && files[0]) {
      const file = files[0];

      // Validate file size based on field
      if (name === 'resume' && file.size > 2 * 1024 * 1024) {
        setError('Resume file size must not exceed 2 MB');
        return;
      }

      if (name !== 'resume' && name !== 'profilePhoto' && file.size > 10 * 1024 * 1024) {
        setError('Document file size must not exceed 10 MB');
        return;
      }

      // Validate file type
      if (name === 'profilePhoto') {
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
        if (!allowedTypes.includes(file.type)) {
          setError('Profile photo must be JPG or PNG');
          return;
        }
      } else {
        const allowedTypes = ['application/pdf'];
        if (!allowedTypes.includes(file.type)) {
          setError('Only PDF files are allowed for documents');
          return;
        }
      }

      setFormData(prev => ({ ...prev, [name]: file }));
      setError('');
    }
  };

  const startResendCountdown = () => {
    setResendCountdown(60);
    const timer = setInterval(() => {
      setResendCountdown(prev => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendOtp = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !emailRegex.test(formData.email)) {
      setOtpError('Please enter a valid email address first');
      return;
    }
    setOtpLoading(true);
    setOtpError('');
    try {
      await authService.sendRegistrationOtp(formData.email);
      setOtpSent(true);
      setOtpValue('');
      startResendCountdown();
    } catch (err) {
      setOtpError(err?.response?.data?.error || 'Failed to send OTP. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpValue || otpValue.length !== 6) { setOtpError('Enter the 6-digit OTP'); return; }
    setOtpLoading(true);
    setOtpError('');
    try {
      await authService.verifyRegistrationOtp(formData.email, otpValue);
      setEmailVerified(true);
      setOtpSent(false);
      setOtpValue('');
    } catch (err) {
      setOtpError(err?.response?.data?.error || 'Incorrect OTP. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleEmailChange = (e) => {
    handleInputChange(e);
    // Reset verification if email changes
    if (emailVerified || otpSent) {
      setEmailVerified(false);
      setOtpSent(false);
      setOtpValue('');
      setOtpError('');
      setResendCountdown(0);
    }
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!formData.name || !formData.passportNumber || !formData.gender || !formData.mobile || !formData.email || !formData.dateOfBirth) {
          setError('Please fill in all required fields');
          return false;
        }
        if (formData.mobile.length < 4 || formData.mobile.length > 15) {
          setError('Mobile number must be between 4 and 15 digits');
          return false;
        }
        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
          setError('Please enter a valid email address');
          return false;
        }
        if (!emailVerified) {
          setError('Please verify your email address with the OTP before continuing');
          return false;
        }
        // Validate password
        if (!formData.password || formData.password.length < 6) {
          setError('Password must be at least 6 characters');
          return false;
        }
        if (formData.password !== formData.confirmPassword) {
          setError('Passwords do not match');
          return false;
        }
        // Validate date of birth (must be at least 18 years old)
        const today = new Date();
        const birthDate = new Date(formData.dateOfBirth);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        if (age < 18) {
          setError('You must be at least 18 years old to register');
          return false;
        }
        if (age > 65) {
          setError('Age must be 65 years or below');
          return false;
        }
        if (!formData.profilePhoto) {
          setError('Please upload a profile photo');
          return false;
        }
        break;
      case 2:
        // Professional details are optional, no validation needed
        break;
      case 3:
        if (!formData.resume) {
          setError('Please upload Resume');
          return false;
        }

        const missingRequiredDocument = REGISTRATION_DOCUMENT_FIELDS.find((doc) => doc.required && !formData[doc.key]);
        if (missingRequiredDocument) {
          setError(`Please upload: ${missingRequiredDocument.label}`);
          return false;
        }
        break;
      default:
        return true;
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
      setError('');
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateStep(currentStep)) {
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Combine country code with mobile number
      const formattedPhoneNumber = `${countryCode}${formData.mobile}`;

      // Strip confirmPassword before sending; include password
      const { confirmPassword, ...rest } = formData;
      const submissionData = {
        ...rest,
        mobile: formattedPhoneNumber
      };

      const response = await authService.registerCandidate(submissionData);

      if (response.success) {
        navigate('/login', {
          state: {
            loginType: 'candidate',
            registrationSuccess: true
          }
        });
      } else {
        setError(response.error || 'Registration failed. Please try again.');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };


  const renderStepIndicator = () => (
    <div className="flex justify-between mb-xl px-0 md:px-lg">
      {[1, 2, 3, 4].map(step => (
        <div key={step} className="flex flex-col items-center flex-1 relative
          [&:not(:last-child)]:after:content-[''] [&:not(:last-child)]:after:absolute 
          [&:not(:last-child)]:after:top-5 [&:not(:last-child)]:after:left-1/2 
          [&:not(:last-child)]:after:w-full [&:not(:last-child)]:after:h-[2px] 
          [&:not(:last-child)]:after:bg-border [&:not(:last-child)]:after:z-[-1]
          [&:not(:last-child).active]:after:bg-accent-green">
          <div className={`w-10 h-10 rounded-full bg-surface border-2 border-border flex items-center 
            justify-center font-semibold text-text-tertiary mb-sm transition-all duration-300
            ${currentStep >= step ? 'border-accent-green text-accent-green bg-[rgba(107,127,92,0.1)]' : ''}
            ${currentStep > step ? 'bg-accent-green border-accent-green text-surface' : ''}`}>
            {currentStep > step ? '✓' : step}
          </div>
          <div className="text-[11px] md:text-[12px] text-text-secondary font-medium">
            {step === 1 && 'Personal'}
            {step === 2 && 'Professional'}
            {step === 3 && 'Documents'}
            {step === 4 && 'Review'}
          </div>
        </div>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div className="mb-lg">
      <h3 className="text-[20px] text-primary mb-lg font-semibold pb-md border-b-2 border-border-light">
        Personal Information
      </h3>

      <div className="mb-lg">
        <label htmlFor="name" className="block mb-sm text-text-primary font-medium text-[14px]">
          Name (As per Passport) *
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          placeholder="Full Name"
          disabled={isLoading}
          autoFocus
          className="w-full py-[14px] px-4 border-[1.5px] border-border rounded-md text-[15px] 
            transition-all duration-200 box-border bg-surface text-text-primary
            focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]
            disabled:bg-background-light disabled:cursor-not-allowed disabled:opacity-60"
        />
      </div>

      <div className="mb-lg">
        <label htmlFor="passportNumber" className="block mb-sm text-text-primary font-medium text-[14px]">
          Passport Number *
        </label>
        <input
          type="text"
          id="passportNumber"
          name="passportNumber"
          value={formData.passportNumber}
          onChange={handleInputChange}
          placeholder="AB1234567"
          disabled={isLoading}
          className="w-full py-[14px] px-4 border-[1.5px] border-border rounded-md text-[15px] 
            transition-all duration-200 box-border bg-surface text-text-primary
            focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]
            disabled:bg-background-light disabled:cursor-not-allowed disabled:opacity-60"
        />
      </div>

      <div className="mb-lg">
        <label htmlFor="gender" className="block mb-sm text-text-primary font-medium text-[14px]">
          Gender *
        </label>
        <select
          id="gender"
          name="gender"
          value={formData.gender}
          onChange={handleInputChange}
          disabled={isLoading}
          className="w-full py-[14px] px-4 pr-10 border-[1.5px] border-border rounded-md text-[15px] 
            transition-all duration-200 box-border bg-surface text-text-primary cursor-pointer 
            appearance-none bg-[url('data:image/svg+xml,%3Csvg%20xmlns=%27http://www.w3.org/2000/svg%27%20width=%2712%27%20height=%2712%27%20viewBox=%270%200%2012%2012%27%3E%3Cpath%20fill=%27%23666%27%20d=%27M6%209L1%204h10z%27/%3E%3C/svg%3E')] 
            bg-no-repeat bg-[right_16px_center]
            focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]
            disabled:bg-background-light disabled:cursor-not-allowed disabled:opacity-60"
        >
          <option value="">-- Select Gender --</option>
          {GENDER_OPTIONS.map(option => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      </div>


      <div className="grid grid-cols-1 md:grid-cols-2 gap-md mb-lg">
        <div>
          <label htmlFor="state" className="block mb-sm text-text-primary font-medium text-[14px]">
            State
          </label>
          <select
            id="state"
            name="state"
            value={formData.state}
            onChange={handleInputChange}
            disabled={isLoading}
            className="w-full py-[14px] px-4 pr-10 border-[1.5px] border-border rounded-md text-[15px] 
              transition-all duration-200 box-border bg-surface text-text-primary cursor-pointer 
              appearance-none bg-[url('data:image/svg+xml,%3Csvg%20xmlns=%27http://www.w3.org/2000/svg%27%20width=%2712%27%20height=%2712%27%20viewBox=%270%200%2012%2012%27%3E%3Cpath%20fill=%27%23666%27%20d=%27M6%209L1%204h10z%27/%3E%3C/svg%3E')] 
              bg-no-repeat bg-[right_16px_center]
              focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]
              disabled:bg-background-light disabled:cursor-not-allowed disabled:opacity-60"
          >
            <option value="">-- Select State --</option>
            {STATES.map(state => (
              <option key={state} value={state}>{state}</option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="district" className="block mb-sm text-text-primary font-medium text-[14px]">
            District
          </label>
          <select
            id="district"
            name="district"
            value={formData.district}
            onChange={handleInputChange}
            disabled={isLoading || !formData.state}
            className="w-full py-[14px] px-4 pr-10 border-[1.5px] border-border rounded-md text-[15px] 
              transition-all duration-200 box-border bg-surface text-text-primary cursor-pointer 
              appearance-none bg-[url('data:image/svg+xml,%3Csvg%20xmlns=%27http://www.w3.org/2000/svg%27%20width=%2712%27%20height=%2712%27%20viewBox=%270%200%2012%2012%27%3E%3Cpath%20fill=%27%23666%27%20d=%27M6%209L1%204h10z%27/%3E%3C/svg%3E')] 
              bg-no-repeat bg-[right_16px_center]
              focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]
              disabled:bg-background-light disabled:cursor-not-allowed disabled:opacity-60"
          >
            <option value="">-- Select District --</option>
            {districts.map(district => (
              <option key={district} value={district}>{district}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="mb-lg">
        <label htmlFor="mobile" className="block mb-sm text-text-primary font-medium text-[14px]">
          Mobile Number (WhatsApp) *
        </label>
        <div className="flex gap-2">
          <select
            id="countryCode"
            value={countryCode}
            onChange={(e) => setCountryCode(e.target.value)}
            disabled={isLoading}
            className="w-[145px] py-[14px] px-3 border-[1.5px] border-border rounded-md text-[15px] 
              transition-all duration-200 box-border bg-surface text-text-primary cursor-pointer 
              appearance-none bg-[url('data:image/svg+xml,%3Csvg%20xmlns=%27http://www.w3.org/2000/svg%27%20width=%2712%27%20height=%2712%27%20viewBox=%270%200%2012%2012%27%3E%3Cpath%20fill=%27%23666%27%20d=%27M6%209L1%204h10z%27/%3E%3C/svg%3E')] 
              bg-no-repeat bg-[right_12px_center]
              focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]
              disabled:bg-background-light disabled:cursor-not-allowed disabled:opacity-60"
          >
            {countryCodeOptions.map((option) => (
              <option key={option.code} value={option.code}>
                {option.label}
              </option>
            ))}
          </select>
          <input
            type="tel"
            id="mobile"
            name="mobile"
            value={formData.mobile}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '');
              if (value.length <= 15) {
                handleInputChange({ target: { name: 'mobile', value } });
              }
            }}
            placeholder="Enter phone number"
            maxLength="15"
            disabled={isLoading}
            className="flex-1 py-[14px] px-4 border-[1.5px] border-border rounded-md text-[15px] 
              transition-all duration-200 box-border bg-surface text-text-primary
              focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]
              disabled:bg-background-light disabled:cursor-not-allowed disabled:opacity-60"
          />
        </div>
        <small className="block mt-sm text-text-tertiary text-[13px] leading-[1.4]">
          Enter your mobile number (4-15 digits, will be used for login)
        </small>
      </div>

      <div className="mb-lg">
        <label htmlFor="email" className="block mb-sm text-text-primary font-medium text-[14px]">
          Email Address *
        </label>
        <div className="flex gap-2">
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleEmailChange}
            placeholder="your.email@example.com"
            disabled={isLoading || otpSent || emailVerified}
            className="flex-1 py-[14px] px-4 border-[1.5px] border-border rounded-md text-[15px]
              transition-all duration-200 box-border bg-surface text-text-primary
              focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]
              disabled:bg-background-light disabled:cursor-not-allowed disabled:opacity-60"
          />
          {!emailVerified && (
            <button
              type="button"
              onClick={handleSendOtp}
              disabled={otpLoading || !formData.email || resendCountdown > 0}
              className="px-4 py-[14px] bg-accent-green text-surface rounded-md text-[14px] font-semibold
                transition-all duration-200 whitespace-nowrap
                hover:bg-accent-green-dark disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {otpLoading && !otpSent ? 'Sending...' : otpSent ? (resendCountdown > 0 ? `Resend (${resendCountdown}s)` : 'Resend OTP') : 'Send OTP'}
            </button>
          )}
        </div>

        {/* Verified badge */}
        {emailVerified && (
          <div className="mt-sm py-2.5 px-3.5 bg-[rgba(107,127,92,0.1)] border-l-[3px] border-accent-green
            rounded-sm text-accent-green text-[13px] font-medium flex items-center justify-between">
            <span>✓ Email verified</span>
            <button
              type="button"
              onClick={() => { setEmailVerified(false); setOtpSent(false); setOtpValue(''); setOtpError(''); }}
              className="text-[12px] underline text-accent-green opacity-70 hover:opacity-100"
            >
              Change email
            </button>
          </div>
        )}

        {/* OTP input */}
        {otpSent && !emailVerified && (
          <div className="mt-md p-md bg-background-light border border-border rounded-md">
            <p className="text-[13px] text-text-secondary mb-sm">
              A 6-digit OTP has been sent to <strong>{formData.email}</strong>. Enter it below.
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={otpValue}
                onChange={e => { setOtpValue(e.target.value.replace(/\D/g, '').slice(0, 6)); setOtpError(''); }}
                placeholder="Enter 6-digit OTP"
                className="flex-1 py-[12px] px-4 border-[1.5px] border-border rounded-md text-[15px]
                  tracking-[0.3em] font-semibold bg-surface text-text-primary text-center
                  focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]"
              />
              <button
                type="button"
                onClick={handleVerifyOtp}
                disabled={otpLoading || otpValue.length !== 6}
                className="px-5 py-[12px] bg-accent-green text-surface rounded-md text-[14px] font-semibold
                  transition-all duration-200 hover:bg-accent-green-dark disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {otpLoading ? 'Verifying...' : 'Verify'}
              </button>
            </div>
            {otpError && (
              <p className="mt-sm text-[13px] text-status-error">{otpError}</p>
            )}
          </div>
        )}

        {otpError && !otpSent && (
          <p className="mt-sm text-[13px] text-status-error">{otpError}</p>
        )}

        {!emailVerified && !otpSent && (
          <small className="block mt-sm text-text-tertiary text-[13px] leading-[1.4]">
            Enter your email and click "Send OTP" to verify it
          </small>
        )}
      </div>

      <div className="mb-lg">
        <label htmlFor="password" className="block mb-sm text-text-primary font-medium text-[14px]">
          Password *
        </label>
        <input
          type="password"
          id="password"
          name="password"
          value={formData.password}
          onChange={handleInputChange}
          placeholder="At least 6 characters"
          disabled={isLoading}
          className="w-full py-[14px] px-4 border-[1.5px] border-border rounded-md text-[15px]
            transition-all duration-200 box-border bg-surface text-text-primary
            focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]
            disabled:bg-background-light disabled:cursor-not-allowed disabled:opacity-60"
        />
        <small className="block mt-sm text-text-tertiary text-[13px] leading-[1.4]">
          You will use this password to log in
        </small>
      </div>

      <div className="mb-lg">
        <label htmlFor="confirmPassword" className="block mb-sm text-text-primary font-medium text-[14px]">
          Confirm Password *
        </label>
        <input
          type="password"
          id="confirmPassword"
          name="confirmPassword"
          value={formData.confirmPassword}
          onChange={handleInputChange}
          placeholder="Repeat your password"
          disabled={isLoading}
          className="w-full py-[14px] px-4 border-[1.5px] border-border rounded-md text-[15px]
            transition-all duration-200 box-border bg-surface text-text-primary
            focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]
            disabled:bg-background-light disabled:cursor-not-allowed disabled:opacity-60"
        />
      </div>

      <div className="mb-lg">
        <label htmlFor="dateOfBirth" className="block mb-sm text-text-primary font-medium text-[14px]">
          Date of Birth *
        </label>
        <input
          type="date"
          id="dateOfBirth"
          name="dateOfBirth"
          value={formData.dateOfBirth}
          onChange={handleInputChange}
          max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
          min={new Date(new Date().setFullYear(new Date().getFullYear() - 65)).toISOString().split('T')[0]}
          disabled={isLoading}
          className="w-full py-[14px] px-4 border-[1.5px] border-border rounded-md text-[15px] 
            transition-all duration-200 box-border bg-surface text-text-primary
            focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]
            disabled:bg-background-light disabled:cursor-not-allowed disabled:opacity-60"
        />
        <small className="block mt-sm text-text-tertiary text-[13px] leading-[1.4]">
          You must be between 18 and 65 years old
        </small>
      </div>

      <div className="mb-lg">
        <label htmlFor="profilePhoto" className="block mb-sm text-text-primary font-medium text-[14px]">
          Profile Photo <span className="text-red-500">*</span>
        </label>
        <input
          type="file"
          id="profilePhoto"
          name="profilePhoto"
          accept=".jpg,.jpeg,.png"
          onChange={handleFileChange}
          disabled={isLoading}
          className="w-full py-3 px-4 border-[1.5px] border-border rounded-md text-[15px] 
            transition-all duration-200 box-border cursor-pointer bg-background-light
            file:py-2 file:px-4 file:bg-accent-green file:text-surface file:border-none 
            file:rounded-sm file:cursor-pointer file:font-semibold file:text-[13px] 
            file:mr-md file:transition-all file:duration-200
            file:hover:bg-accent-green-dark
            focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]
            disabled:bg-background-light disabled:cursor-not-allowed disabled:opacity-60"
        />
        {formData.profilePhoto && (
          <div className="mt-sm py-2.5 px-3.5 bg-[rgba(107,127,92,0.1)] border-l-[3px] border-accent-green 
            rounded-sm text-accent-green text-[13px] font-medium">
            ✓ {formData.profilePhoto.name}
          </div>
        )}
        <small className="block mt-sm text-text-tertiary text-[13px] leading-[1.4]">
          Allowed formats: JPG, PNG
        </small>
      </div>
    </div>
  );


  const renderStep2 = () => (
    <div className="mb-lg">
      <h3 className="text-[20px] text-primary mb-lg font-semibold pb-md border-b-2 border-border-light">
        Professional Details
      </h3>

      <div className="mb-lg">
        <label htmlFor="currentJobRole" className="block mb-sm text-text-primary font-medium text-[14px]">
          Current Job Role
        </label>
        <input
          type="text"
          id="currentJobRole"
          name="currentJobRole"
          value={formData.currentJobRole}
          onChange={handleInputChange}
          placeholder="e.g., Staff Nurse, General Practitioner"
          disabled={isLoading}
          autoFocus
          className="w-full py-[14px] px-4 border-[1.5px] border-border rounded-md text-[15px] 
            transition-all duration-200 box-border bg-surface text-text-primary
            focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]
            disabled:bg-background-light disabled:cursor-not-allowed disabled:opacity-60"
        />
      </div>

      <div className="mb-lg">
        <label htmlFor="workExperience" className="block mb-sm text-text-primary font-medium text-[14px]">
          Work Experience (Years)
        </label>
        <select
          id="workExperience"
          name="workExperience"
          value={formData.workExperience}
          onChange={handleInputChange}
          disabled={isLoading}
          className="w-full py-[14px] px-4 pr-10 border-[1.5px] border-border rounded-md text-[15px] 
            transition-all duration-200 box-border bg-surface text-text-primary cursor-pointer 
            appearance-none bg-[url('data:image/svg+xml,%3Csvg%20xmlns=%27http://www.w3.org/2000/svg%27%20width=%2712%27%20height=%2712%27%20viewBox=%270%200%2012%2012%27%3E%3Cpath%20fill=%27%23666%27%20d=%27M6%209L1%204h10z%27/%3E%3C/svg%3E')] 
            bg-no-repeat bg-[right_16px_center]
            focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]
            disabled:bg-background-light disabled:cursor-not-allowed disabled:opacity-60"
        >
          <option value="">-- Select Experience --</option>
          {WORK_EXPERIENCE_OPTIONS.map(option => (
            <option key={option} value={option}>{option} {option === '25+' ? '' : 'years'}</option>
          ))}
        </select>
      </div>

      <div className="mb-lg">
        <label htmlFor="education" className="block mb-sm text-text-primary font-medium text-[14px]">
          Education
        </label>
        <select
          id="education"
          name="education"
          value={formData.education}
          onChange={handleInputChange}
          disabled={isLoading}
          className="w-full py-[14px] px-4 pr-10 border-[1.5px] border-border rounded-md text-[15px] 
            transition-all duration-200 box-border bg-surface text-text-primary cursor-pointer 
            appearance-none bg-[url('data:image/svg+xml,%3Csvg%20xmlns=%27http://www.w3.org/2000/svg%27%20width=%2712%27%20height=%2712%27%20viewBox=%270%200%2012%2012%27%3E%3Cpath%20fill=%27%23666%27%20d=%27M6%209L1%204h10z%27/%3E%3C/svg%3E')] 
            bg-no-repeat bg-[right_16px_center]
            focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]
            disabled:bg-background-light disabled:cursor-not-allowed disabled:opacity-60"
        >
          <option value="">-- Select Education --</option>
          {EDUCATION_OPTIONS.map(option => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      </div>
    </div>
  );


  const renderStep3 = () => (
    <div className="mb-lg">
      <h3 className="text-[20px] text-primary mb-lg font-semibold pb-md border-b-2 border-border-light">
        Documents
      </h3>

      <div className="mb-lg">
        <label htmlFor="resume" className="block mb-sm text-text-primary font-medium text-[14px]">
          Resume *
        </label>
        <input
          type="file"
          id="resume"
          name="resume"
          accept=".pdf"
          onChange={handleFileChange}
          disabled={isLoading}
          className="w-full py-3 px-4 border-[1.5px] border-border rounded-md text-[15px] 
            transition-all duration-200 box-border cursor-pointer bg-background-light
            file:py-2 file:px-4 file:bg-accent-green file:text-surface file:border-none 
            file:rounded-sm file:cursor-pointer file:font-semibold file:text-[13px] 
            file:mr-md file:transition-all file:duration-200
            file:hover:bg-accent-green-dark
            focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]
            disabled:bg-background-light disabled:cursor-not-allowed disabled:opacity-60"
        />
        {formData.resume && (
          <div className="mt-sm py-2.5 px-3.5 bg-[rgba(107,127,92,0.1)] border-l-[3px] border-accent-green 
            rounded-sm text-accent-green text-[13px] font-medium">
            ✓ {formData.resume.name} ({(formData.resume.size / 1024 / 1024).toFixed(2)} MB)
          </div>
        )}
        <small className="block mt-sm text-text-tertiary text-[13px] leading-[1.4]">
          Max file size: 2 MB
        </small>
      </div>

      <div className="mb-lg">
        <h4 className="text-[16px] font-semibold text-primary mb-sm">Upload Documents Separately</h4>
        <p className="m-0 mb-md text-text-secondary text-[13px]">Each document must be uploaded in its own field (PDF, max 10 MB each).</p>

        <div className="space-y-md">
          {REGISTRATION_DOCUMENT_FIELDS.map((docField) => (
            <div key={docField.key}>
              <label htmlFor={docField.key} className="block mb-sm text-text-primary font-medium text-[14px]">
                {docField.label} {docField.required ? '*' : '(Optional)'}
              </label>
              <input
                type="file"
                id={docField.key}
                name={docField.key}
                accept=".pdf"
                onChange={handleFileChange}
                disabled={isLoading}
                className="w-full py-3 px-4 border-[1.5px] border-border rounded-md text-[15px] 
                  transition-all duration-200 box-border cursor-pointer bg-background-light
                  file:py-2 file:px-4 file:bg-accent-green file:text-surface file:border-none 
                  file:rounded-sm file:cursor-pointer file:font-semibold file:text-[13px] 
                  file:mr-md file:transition-all file:duration-200
                  file:hover:bg-accent-green-dark
                  focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]
                  disabled:bg-background-light disabled:cursor-not-allowed disabled:opacity-60"
              />
              {formData[docField.key] && (
                <div className="mt-sm py-2.5 px-3.5 bg-[rgba(107,127,92,0.1)] border-l-[3px] border-accent-green 
                  rounded-sm text-accent-green text-[13px] font-medium">
                  ✓ {formData[docField.key].name} ({(formData[docField.key].size / 1024 / 1024).toFixed(2)} MB)
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );


  const renderStep4 = () => (
    <div className="mb-lg">
      <h3 className="text-[20px] text-primary mb-lg font-semibold pb-md border-b-2 border-border-light">
        Review & Submit
      </h3>

      <div className="space-y-md">
        <div className="p-md bg-background-light rounded-md border border-border-light">
          <h4 className="text-[16px] font-semibold text-primary mb-sm">Personal Information</h4>
          <div className="space-y-xs text-[14px]">
            <p><span className="font-medium">Name:</span> {formData.name}</p>
            <p><span className="font-medium">Passport Number:</span> {formData.passportNumber}</p>
            <p><span className="font-medium">Gender:</span> {formData.gender}</p>
            {formData.state && <p><span className="font-medium">Location:</span> {formData.district ? `${formData.district}, ${formData.state}` : formData.state}</p>}
            <p><span className="font-medium">Mobile:</span> {countryCode}{formData.mobile}</p>
            <p><span className="font-medium">Email:</span> {formData.email}</p>
            <p><span className="font-medium">Date of Birth:</span> {formData.dateOfBirth ? new Date(formData.dateOfBirth).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : ''}</p>
            {formData.profilePhoto && <p><span className="font-medium">Profile Photo:</span> ✓ Uploaded</p>}
          </div>
        </div>

        <div className="p-md bg-background-light rounded-md border border-border-light">
          <h4 className="text-[16px] font-semibold text-primary mb-sm">Professional Details</h4>
          <div className="space-y-xs text-[14px]">
            {formData.currentJobRole && <p><span className="font-medium">Job Role:</span> {formData.currentJobRole}</p>}
            {formData.workExperience && <p><span className="font-medium">Experience:</span> {formData.workExperience} years</p>}
            {formData.education && <p><span className="font-medium">Education:</span> {formData.education}</p>}
            {!formData.currentJobRole && !formData.workExperience && !formData.education && (
              <p className="text-text-tertiary">No professional details provided</p>
            )}
          </div>
        </div>

        <div className="p-md bg-background-light rounded-md border border-border-light">
          <h4 className="text-[16px] font-semibold text-primary mb-sm">Documents</h4>
          <div className="space-y-xs text-[14px] mb-sm">
            <p><span className="font-medium">Resume:</span> {formData.resume ? `✓ ${formData.resume.name}` : '✗ Not uploaded'}</p>
          </div>
          <div className="space-y-xs text-[13px] text-text-secondary">
            {REGISTRATION_DOCUMENT_FIELDS.map((docField) => (
              <p key={docField.key}>
                <span className="font-medium text-text-primary">{docField.label}:</span> {formData[docField.key] ? `✓ ${formData[docField.key].name}` : (docField.required ? '✗ Not uploaded' : '- Optional not uploaded')}
              </p>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex justify-center items-center min-h-screen bg-background p-lg relative
      before:content-[''] before:absolute before:top-0 before:left-0 before:right-0 before:h-[40%] 
      before:bg-gradient-to-br before:from-accent-green before:to-accent-green-dark before:z-0">
      <div className="bg-surface p-lg md:p-2xl rounded-lg shadow-lg w-full max-w-[600px] relative z-[1] 
        border border-border-light">
        <h1 className="text-[28px] text-primary mb-sm text-center font-semibold tracking-[-0.5px]">
          Candidate Registration
        </h1>
        <h2 className="text-[15px] text-text-secondary mb-xl text-center font-normal leading-[1.5]">
          Join our overseas recruitment program
        </h2>

        {renderStepIndicator()}

        <form onSubmit={handleSubmit}>
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}

          {error && (
            <div className="bg-status-error-bg text-status-error py-[14px] px-4 rounded-md mb-lg 
              text-[14px] border-l-[3px] border-status-error leading-[1.5]">
              {error}
            </div>
          )}

          <div className="flex gap-md mt-xl">
            {currentStep > 1 && (
              <button
                type="button"
                className="flex-1 py-[14px] px-6 bg-surface text-text-primary border-[1.5px] border-border 
                  rounded-md text-[15px] font-semibold cursor-pointer transition-all duration-200 shadow-sm
                  hover:bg-background-light hover:border-accent-green hover:-translate-y-[1px] hover:enabled:bg-background-light
                  active:translate-y-0 active:enabled:translate-y-0
                  disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                onClick={handlePrevious}
                disabled={isLoading}
              >
                Previous
              </button>
            )}

            {currentStep < totalSteps ? (
              <button
                type="button"
                className="flex-1 py-[14px] px-6 bg-accent-green text-surface border-none rounded-md 
                  text-[15px] font-semibold cursor-pointer transition-all duration-200 shadow-sm
                  hover:bg-accent-green-dark hover:-translate-y-[1px] hover:shadow-md hover:enabled:bg-accent-green-dark
                  active:translate-y-0 active:enabled:translate-y-0
                  disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                onClick={handleNext}
                disabled={isLoading}
              >
                Next
              </button>
            ) : (
              <button
                type="submit"
                className="flex-1 py-[14px] px-6 bg-accent-green text-surface border-none rounded-md 
                  text-[15px] font-semibold cursor-pointer transition-all duration-200 shadow-sm
                  hover:bg-accent-green-dark hover:-translate-y-[1px] hover:shadow-md hover:enabled:bg-accent-green-dark
                  active:translate-y-0 active:enabled:translate-y-0
                  disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                disabled={isLoading}
              >
                {isLoading ? 'Registering...' : 'Complete Registration'}
              </button>
            )}
          </div>
        </form>

        <div className="text-center mt-xl pt-lg border-t border-border-light text-text-secondary text-[14px]">
          Already have an account? <a href="/" className="text-accent-green no-underline font-semibold 
            transition-colors duration-200 hover:text-accent-green-dark hover:underline">Login here</a>
        </div>
      </div>
    </div>
  );
};

export default CandidateRegistrationPage;
